import {
  videoActions,
  reportGifByIdSuccess,
  reportGifByIdError,
} from "./video/video-action";
import { all, takeLatest, put, delay } from "redux-saga/effects";
import { apiCollections, apiMain, apiNiches, apiTags, apiSearch } from "services";
import { IOperation } from "services/RedGifsService";
import {
  IAddGifParamsToCollection,
  ICreateCollectionParams,
  IEditCollectionParams,
} from "services/CollectionService";
import { IAddToNicheParams } from "services/NichesService";
import {
  ICollection,
  ICollectionSearchResults,
  IExperimentList,
  INichesItems,
  ITagsList,
} from "services/types";
import { IAppAction } from "./appAction";
import { fetchHomeError, fetchHomeSuccess, homeActions } from "./home/home-action";
import {
  exploreActions,
  fetchAllTagsError,
  fetchAllTagsSuccess,
} from "./explore/explore-action";

import {
  setServerError,
  setServerResponse
} from "./application/application-action";
import {
  addGifToCollectionError,
  addGifToCollectionSuccess,
  collectionsActions,
  createCollectionError,
  createCollectionSuccess,
  editCollectionError,
  editCollectionSuccess,
  fetchCollectionError,
  fetchCollectionsSuccess,
  fetchCurrentCollectionError,
  fetchCurrentCollectionSuccess,
  fetchMyCollections,
  fetchMyCurrentCollection,
  setCollectionsOwnerName,
  setEmptyCollections,
  setNewCollectionId,
  setTotalCollections,
} from "./collections/collections-action";

import {
  fetchTagsError,
  fetchTagsSuccess,
  tagsActions
} from "./tags/tags-action";

import { addToNicheError, addToNicheSuccess, fetchNichesError, fetchNichesSuccess, nichesActions } from "./niches/niches-action";
import {
  fetchCurrentUserError,
  fetchCurrentUserSuccess,
  updateUserProfileError,
  updateUserProfileSuccess,
  userActions
} from "./user/user-action";
import {
  searchActions,
  setTrendingSearchesSuccess,
  setTrendingSearchesError,
  setCreatorsSearchesSuccess,
  setCreatorsSearchesError,
  setNichesSearchesSuccess,
  setNichesSearchesError,
  setTagsSearchesSuccess,
  setTagsSearchesError,
  setHistorySearchesSuccess,
  setHistorySearchesError,
} from "./search/search-action";

import {
  experimentsActions,
  fetchExperimentsSuccess,
  fetchExperimentsError,
} from "./experiments/actions";

import { TakeableChannel } from "redux-saga";
import { AxiosError } from "axios";

function* fetchHome() {
  try {
    const response: {} = yield apiMain.fetchHome();
    yield put(fetchHomeSuccess(response));
  } catch (error) {
    yield put(fetchHomeError(error as Error));
  }
}

function* fetchAllTags() {
  try {
    const response: {} = yield apiMain.fetchAllTags();
    yield put(fetchAllTagsSuccess(response));
  } catch (error) {
    yield put(fetchAllTagsError(error as Error));
  }
}

function* reportGif(params: IAppAction) {
  try {
    const response: {} = yield apiMain.reportGif(params.payload);
    yield put(reportGifByIdSuccess(response));
    yield put(setServerResponse("Report has been sent"));
  } catch (error) {
    yield put(reportGifByIdError(error as Error));
    yield put(setServerError(error as Error));
  }
}

function* getMyCollections(params: IAppAction) {
  try {
    const response: ICollectionSearchResults = yield apiCollections.getMyCollections({
      page: params.payload.page,
      count: params.payload.count,
    });
    yield put(fetchCollectionsSuccess(response.collections));
    yield put(setTotalCollections(response.totalCount));
    yield put(setCollectionsOwnerName(response.users[0].name));
  } catch (error) {
    yield put(fetchCollectionError(error as Error));
  }
}

function* getMyCollectionById(params: IAppAction) {
  try {
    const response: ICollection = yield apiCollections.getMyCollectionById({
      collectionId: params.payload.collectionId
    });
    yield put(fetchCurrentCollectionSuccess(response));
  } catch (error) {
    yield put(fetchCurrentCollectionError(error as Error));
  }
}

function* getTags() {
  try {
    const response: { tags: any } = yield apiTags.getTags();
    yield put(fetchTagsSuccess(response.tags));
  } catch (error) {
    yield put(fetchTagsError(error as Error));
  }
}

function* getNiches() {
  try {
    const response: {} = yield apiNiches.getNiches({
      previews: true,
    });

    yield put(fetchNichesSuccess(response));
  } catch (error) {
    yield put(fetchNichesError(error as Error));
  }
}

function* fetchCurrentUser() {
  try {
    const response: {} = yield apiMain.getMe();
    yield put(fetchCurrentUserSuccess(response));
  } catch (error) {
    yield put(fetchCurrentUserError(error as Error));
  }
}

function* updateUserProfile({ payload }: {
  type: TakeableChannel<string>;
  payload: IOperation[];
}) {
  try {
    yield apiMain.updateProfileInfo(payload);
    yield put(updateUserProfileSuccess());
    yield put(setServerResponse("Successfully updated!"));
  } catch (error) {
    const err = error as AxiosError<any>;
    yield put(updateUserProfileError(error as Error));
    yield put(setServerError((err.response && err.response.data ? err.response.data.error : error) as Error));
  }
}

function* deleteMyAccount() {
  try {
    yield apiMain.deleteMyAccount();
    yield put(setServerResponse("Your account was successfully removed!"));
  } catch (error) {
    yield put(setServerError(error as Error));
  }
}

function* updateProfilePhoto(data: any) {
  try {
    yield apiMain.updateProfilePhoto(data.payload);
    yield put(setServerResponse("Successfully updated!"));
  } catch (error) {
    const err = error as AxiosError<any>;
    yield put(setServerError(err?.response?.data?.error || err));
  }
}

function* createCollection ({ payload }: {
  type: TakeableChannel<string>;
  payload: ICreateCollectionParams;
}) {
  try {
    const response: ICollection = yield apiCollections.createCollection(payload);
    yield put(createCollectionSuccess(response));
    yield put(setNewCollectionId(response.folderId));
    yield put(setEmptyCollections());
    yield put(fetchMyCollections({
      page: 1,
      count: 80
    }));
    yield put(setServerResponse("Successfully created."));
  } catch (error) {
    yield put(createCollectionError(error as Error));
  }
}

function* editCollection ({ payload }: {
  type: TakeableChannel<string>;
  payload: IEditCollectionParams;
}) {
  try {
    const response: ICollection = yield apiCollections.editCollection(payload);
    yield put(editCollectionSuccess(response));
    yield put(fetchMyCurrentCollection({
      collectionId: payload.collectionId
    }));
    yield put(setServerResponse("Successfully updated."));
  } catch (error) {
    yield put(editCollectionError(error as Error));
  }
}

function* addGifToCollection ({ payload }: {
  type: TakeableChannel<string>;
  payload: IAddGifParamsToCollection;
}) {
  try {
    const response: {} = yield apiCollections.addGifToCollection(payload);
    yield put(addGifToCollectionSuccess(response));
    yield put(setServerResponse("Added to Collection('s)!"));
    // eslint-disable-next-line no-magic-numbers
    yield delay(3000);
    yield put(setServerResponse(null));
  } catch (error) {
    yield put(addGifToCollectionError(error as Error));
  }
}

function* addToNiche ({ payload }: {
  type: TakeableChannel<string>;
  payload: IAddToNicheParams;
}) {
  try {
    const response: {} = yield apiNiches.addToNiche(payload);
    yield put(addToNicheSuccess(response));
    yield put(setServerResponse("Add to Niche request sended."));
  } catch (error) {
    yield put(addToNicheError(error as Error));
  }
}

function* fetchTrendingSearches() {
  try {
    const response: { tags: any } = yield apiSearch.getTrendingSearches();
    yield put(setTrendingSearchesSuccess(response.tags));
  } catch (error) {
    yield put(setTrendingSearchesError(error as Error));
  }
}

function* fetchExperiments({ payload }: {
  type: TakeableChannel<string>;
  payload: any;
}) {
  try {
    const response: IExperimentList = yield apiMain.getExperiments();
    yield put(fetchExperimentsSuccess(response));
  } catch (error) {
    yield put(fetchExperimentsError(error as Error));
  }
}

function* fetchHistorySearches({ payload }: {
  type: TakeableChannel<string>;
  payload: string;
}) {
  try {
    const response: { tags: any } = yield apiSearch.getHistorySearches(payload);
    yield put(setHistorySearchesSuccess(response));
  } catch (error) {
    yield put(setHistorySearchesError(error as Error));
  }
}

function* fetchCreatorsSearches({ payload }: {
  type: TakeableChannel<string>;
  payload: string;
}) {
  try {
    const response: { items: any } = yield apiSearch.getCreatorsSearches(payload);
    yield put(setCreatorsSearchesSuccess(response.items));
  } catch (error) {
    yield put(setCreatorsSearchesError(error as Error));
  }
}

function* fetchNichesSearches({ payload }: {
  type: TakeableChannel<string>;
  payload: string;
}) {
  try {
    const response: INichesItems[] = yield apiSearch.getNichesSearches(payload);
    yield put(setNichesSearchesSuccess(response));
  } catch (error) {
    yield put(setNichesSearchesError(error as Error));
  }
}

function* fetchTagsSearches({ payload }: {
  type: TakeableChannel<string>;
  payload: string;
}) {
  try {
    const response: ITagsList[] = yield apiSearch.getTagsSearches(payload);
    yield put(setTagsSearchesSuccess(response));
  } catch (error) {
    yield put(setTagsSearchesError(error as Error));
  }
}

export default function* rootSaga() {
  yield all([
    takeLatest(
      homeActions.FETCH_HOME,
      fetchHome
    ),
    takeLatest(
      exploreActions.FETCH_ALL_TAGS,
      fetchAllTags
    ),
    takeLatest(
      experimentsActions.FETCH_EXPERIMENTS,
      fetchExperiments,
    ),
    takeLatest(
      videoActions.REPORT_GIF_BY_ID,
      reportGif
    ),
    takeLatest(
      collectionsActions.FETCH_MY_COLLECTIONS,
      getMyCollections
    ),
    takeLatest(
      collectionsActions.FETCH_MY_CURRENT_COLLECTION,
      getMyCollectionById
    ),
    takeLatest(
      collectionsActions.CREATE_COLLECTION,
      createCollection
    ),
    takeLatest(
      collectionsActions.EDIT_COLLECTION,
      editCollection
    ),
    takeLatest(
      collectionsActions.ADD_GIF_TO_COLLECTION,
      addGifToCollection
    ),
    takeLatest(
      tagsActions.FETCH_TAGS,
      getTags
    ),
    takeLatest(
      nichesActions.FETCH_NICHES,
      getNiches
    ),
    takeLatest(
      nichesActions.ADD_TO_NICHE,
      addToNiche
    ),
    takeLatest(
      userActions.FETCH_CURRENT_USER,
      fetchCurrentUser
    ),
    takeLatest(
      userActions.UPDATE_USER_PROFILE,
      updateUserProfile
    ),
    takeLatest(
      userActions.UPDATE_PROFILE_PHOTO,
      updateProfilePhoto
    ),
    takeLatest(
      userActions.DELETE_MY_ACCOUNT,
      deleteMyAccount
    ),
    takeLatest(
      searchActions.GET_TRENDING_SEARCHES,
      fetchTrendingSearches
    ),
    takeLatest(
      searchActions.GET_CREATORS_SEARCHES,
      fetchCreatorsSearches
    ),
    takeLatest(
      searchActions.GET_NICHES_SEARCHES,
      fetchNichesSearches
    ),
    takeLatest(
      searchActions.GET_TAGS_SEARCHES,
      fetchTagsSearches
    ),
    takeLatest(
      searchActions.GET_HISTORY_SEARCHES,
      fetchHistorySearches
    ),
  ]);
}
